import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, EMPTY, Observable, Subscription } from "rxjs";
import { catchError, filter, finalize, map, skip, tap } from "rxjs/operators";
import {
  ColumnSorterInfo,
  ConfigSettingType,
  DefaultPage,
  FieldType,
  FilterEvent,
  PageEvent,
  SearchEvent,
  SortEvent,
  TableConfig,
  TableEvent,
} from "src/app/models/custom-table/custom-table.model";
import { SiteDTO } from "src/app/models/Sites/siteDTO";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { SitesService } from "src/app/shared/services/sites/sites.service";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";
import {
  getColorByBgColor,
  getColorFromStr,
} from "src/app/shared/utils/colorUtils";
import { getSitesList } from "src/app/store/actions/contracts/contracts.actions";
import { AppState, sitesStateSitesList } from "src/app/store/reducers";

const USAGE_CODE = "SELECT_SITES";

@Component({
  selector: "app-select-sites-dialog",
  templateUrl: "./select-sites-dialog.component.html",
  styleUrls: ["./select-sites-dialog.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class SelectSitesDialogComponent implements OnInit, OnDestroy {
  public isLoading$ = new BehaviorSubject<boolean>(false);
  public totalSites$ = new BehaviorSubject<number>(0);

  public columnInfo$: Observable<ColumnSorterInfo[]>;

  public sitesList: SiteDTO[];
  public preselectedCodes: string[] = [];

  siteListSub: Subscription;

  private selectedSitesMap: { [code: string]: SiteDTO } = {};
  public selectedRow: object = {};

  public selectedSites: SiteDTO[] = [];

  tableConfig: TableConfig[] = [
    {
      header: "CustomGrid.Options",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS,
    },
    {
      header: "SiteManagement.Code",
      field: "code",
      fieldType: FieldType.STRING,
      hidden: true,
      identifier: true,
    },
    {
      header: "SiteManagement.SiteNumber",
      field: "siteNumber",
      fieldType: FieldType.STRING,
      searchable: true,
      style: "font-weight: 600",
    },
    {
      header: "SiteManagement.Description",
      field: "description",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "SiteManagement.RegionDescription",
      field: "region.description",
      fieldType: FieldType.CHIP_STRING,
      searchable: true,
    },
    {
      header: "SiteManagement.Profile",
      field: "siteProfile.description",
      fieldType: FieldType.CHIP_STRING,
    },
    {
      header: "SiteManagement.Enabled",
      field: "enabled",
      fieldType: FieldType.BOOLEAN,
    },
    {
      header: "SiteGroup.siteGroup",
      field: "siteGroup.description",
      fieldType: FieldType.STRING,
    },
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: any,
    private tableConfigService: TableConfigService,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    private dialogRef: MatDialogRef<SelectSitesDialogComponent>,
    private sitesService: SitesService
  ) {}

  ngOnInit(): void {
    this.columnInfo$ = this.getTableConfig();

    if (this.data?.preselectedSites?.length) {
      this.data.preselectedSites.forEach((s) => {
        this.selectedSitesMap[s.code] = s;
      });
      this.selectedSites = Object.values(this.selectedSitesMap);
      this.preselectedCodes = this.selectedSites.map((p) => p.code);
    }

    this.loadTableData();
  }

  loadTableData(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.siteListSub = this.sitesService
      .getSites(
        {
          skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
          top: pageSize ?? DefaultPage.PAGE_SIZE,
          realSize: true,
        },
        searchEvent,
        sortEvent,
        filterEvent
      )
      .pipe(
        map(({ result }) => {
          this.totalSites$.next(result.size);
          return result.items;
        })
      )
      .subscribe((sites: any[]) => {
        this.sitesList = sites;

        if (sites) {
          this.sitesList = this.sitesList.map((site) => {
            let processedSite = site;
            processedSite = this.setCustomSiteDetails(processedSite);
            return processedSite;
          });
        }
      });
  }

  setCustomSiteDetails(siteDetail: SiteDTO) {
    const customSiteDetail = {
      ...siteDetail,
    };

    var regionDescriptionData = null;
    if (siteDetail?.region?.description) {
      regionDescriptionData = {
        value: siteDetail.region.description,
        icon: "place",
        style:
          `background-color: ` +
          getColorFromStr(siteDetail.region?.code) +
          `; color: ` +
          getColorByBgColor(siteDetail.region?.code) +
          `;`,
      };
    }

    var siteProfileDescriptionData = null;
    if (siteDetail?.siteProfile?.description) {
      siteProfileDescriptionData = {
        value: siteDetail.siteProfile.description,
        //icon: "local_gas_station",
        style:
          `background-color: ` +
          getColorFromStr(siteDetail.siteProfile?.code) +
          `; color: ` +
          getColorByBgColor(siteDetail.siteProfile?.code) +
          `;`,
      };
    }

    return {
      ...customSiteDetail,
      region: {
        ...customSiteDetail.region,
        description: regionDescriptionData,
      },
      siteProfile: {
        ...customSiteDetail.siteProfile,
        description: siteProfileDescriptionData,
      },
    };
  }

  getTableConfig() {
    this.isLoading$.next(true);
    return this.tableConfigService
      .getConfigSettings(USAGE_CODE, ConfigSettingType.TABLE_CONFIG)
      .pipe(
        map(
          (response) =>
            <ColumnSorterInfo[]>response.result?.value?.tableColumnInfo
        ),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant("TableConfig.LoadError"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      );
  }

  onGetTableData(tableEvent: TableEvent) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent;
    this.loadTableData(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  public onIdsUpdate(allIds: string[]) {
    const idSet = new Set(allIds);
    Object.keys(this.selectedSitesMap).forEach((code) => {
      if (!idSet.has(code)) {
        delete this.selectedSitesMap[code];
      }
    });
    this.selectedSites = Object.values(this.selectedSitesMap);
  }

  public onRowSelectionMultiple(currentPageSelected: SiteDTO[]): void {
    if (!this.sitesList) return;

    const currentPageSelectedCodes = new Set(
      (currentPageSelected || []).map((p) => p.code)
    );

    this.sitesList.forEach((p) => {
      if (
        this.selectedSitesMap[p.code] &&
        !currentPageSelectedCodes.has(p.code)
      ) {
        delete this.selectedSitesMap[p.code];
      }
    });

    currentPageSelected.forEach((p) => {
      this.selectedSitesMap[p.code] = p;
    });

    this.selectedSites = Object.values(this.selectedSitesMap);
  }

  onConfirmClick() {
    this.dialogRef.close(this.selectedSites);
  }

  isSelectedRowEmpty(): boolean {
    return Object.keys(this.selectedSitesMap).length === 0;
  }

  ngOnDestroy(): void {
    if (this.siteListSub) {
      this.siteListSub.unsubscribe;
    }
  }
}
